import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { api, ApiError } from '@/lib/api';
import { money } from '@/lib/format';
import { ErrorNote } from '@/components/Layout';
import { Banknote, RotateCcw } from 'lucide-react';
import { settleArgs, settleError, SettleForm, toPence } from './Settle';

/** A small action under a ledger line: a small button, the same for every one. */
export const ROW_ACTION = 'btn-small';

export interface RefundablePayment { payment_id: string; method: string; provider_ref: string | null; amount_pence: number; outstanding_pence: number; description: string | null; status: string }

const PROVIDER_NAME: Record<string, string> = { gocardless: 'GoCardless', paypal: 'PayPal' };
const REFUND_ERRORS: Record<string, string> = {
  over_refund: 'That is more than the refundable amount on this payment.',
  not_refundable: 'This payment has not been received, so there is nothing to refund.',
  reason_required: 'Give a reason. The household sees it.',
  use_provider_route: 'Choose a refund method.',
  bad_date: 'The date cannot be in the future.',
};
const refundError = (e: unknown) => e instanceof ApiError
  ? (e.code === 'provider_refused' ? `The refund was refused: ${e.reason ?? 'no reason given'}.` : REFUND_ERRORS[e.code] ?? 'Could not refund. Nothing was sent; please try again.')
  : e instanceof Error ? (REFUND_ERRORS[Object.keys(REFUND_ERRORS).find(k => e.message.includes(k)) ?? ''] ?? `Could not refund: ${e.message}`) : 'Could not refund.';

/** Give back all or part of a payment: through the provider it came from, or by hand (cash, bank transfer). Optionally cancel the bookings too. */
export function RefundForm({ payment, registrationIds, onDone, onCancel }: { payment: RefundablePayment; registrationIds?: string[]; onDone: () => void; onCancel: () => void }) {
  const left = useQuery({
    queryKey: ['refundable', payment.payment_id],
    queryFn: async () => { const { data, error } = await supabase.rpc('refundable_pence', { p_payment: payment.payment_id }); if (error) throw error; return data as number; },
  });
  const online = (payment.method === 'gocardless' || payment.method === 'paypal') && !!payment.provider_ref;
  const [how, setHow] = useState<'provider' | 'cash' | 'bank_transfer'>(online ? 'provider' : 'cash');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [cancel, setCancel] = useState(!!registrationIds?.length);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const max = left.data ?? 0;
  const pence = amount ? toPence(amount) : max;

  async function save() {
    if (!reason.trim()) { setError('Give a reason. The household sees it.'); return; }
    if (pence < 1 || pence > max) { setError(`Refund between £0.01 and ${money(max)}.`); return; }
    setBusy(true); setError('');
    try {
      if (how === 'provider') {
        const r = await api<{ status: string }>('/admin/refunds', { method: 'POST', body: { payment_id: payment.payment_id, amount_pence: pence, reason: reason.trim() } });
        setDone(r.status === 'paid' ? 'Refunded.' : `Refund requested from ${PROVIDER_NAME[payment.method]}. It shows as pending until they confirm it, and the household is emailed then.`);
      } else {
        const { error } = await supabase.rpc('admin_refund', { p_payment: payment.payment_id, p_amount_pence: pence, p_reason: reason.trim(), p_method: how, p_reference: reference.trim() || null });
        if (error) throw new Error(error.message);
        setDone('Refund recorded.');
      }
      if (cancel && registrationIds?.length) {
        const { error } = await supabase.rpc('admin_cancel_registrations', { p_ids: registrationIds, p_after_refund: true });
        if (error) throw new Error(error.message);
      }
      onDone();
    } catch (e) { setError(refundError(e)); }
    finally { setBusy(false); }
  }

  if (done) return <div className="rounded-sm bg-stone-50 p-4 text-[15px]">{done}<button className="btn-ghost ml-3 border border-stone-300 text-sm" onClick={onCancel}>Close</button></div>;
  return (
    <div className="space-y-3 rounded-sm bg-stone-50 p-4">
      <p className="font-semibold">Refund <span className="font-normal text-ink-500">· {money(payment.amount_pence)} received, {left.isLoading ? '…' : money(max)} refundable</span></p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block"><span className="label">Refund method</span>
          <select className="input" value={how} onChange={e => setHow(e.target.value as typeof how)}>
            {online && <option value="provider">{PROVIDER_NAME[payment.method]}</option>}
            <option value="cash">Cash</option><option value="bank_transfer">Bank transfer</option>
          </select></label>
        <label className="block"><span className="label">Amount £</span><input className="input" inputMode="decimal" placeholder={(max / 100).toFixed(2)} value={amount} onChange={e => setAmount(e.target.value.replace(/[^\d.]/g, ''))} /></label>
        {how !== 'provider' && <label className="block"><span className="label">Reference (optional)</span><input className="input" value={reference} onChange={e => setReference(e.target.value)} /></label>}
        <label className="block sm:col-span-2 lg:col-span-4"><span className="label">Reason (the household sees this)</span><input className="input" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. event cancelled" /></label>
        {!!registrationIds?.length && <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" className="accent-brand-600" checked={cancel} onChange={e => setCancel(e.target.checked)} /> Also cancel {registrationIds.length === 1 ? 'the booking' : `the ${registrationIds.length} bookings`} and release the places</label>}
      </div>
      {how === 'provider' && <p className="text-xs text-ink-500">Refunded to the original card or account. {PROVIDER_NAME[payment.method]} confirms it within a few days.</p>}
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="flex gap-2">
        <button className="btn-primary" disabled={busy || left.isLoading || max === 0} onClick={save}>{busy ? 'Refunding…' : `Refund ${money(pence || 0)}`}</button>
        <button className="btn-ghost border border-stone-300" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export type PaymentAction = 'refund' | 'balance';

/**
 * The actions under a ledger line. They only open a form; the table shows it in a
 * full-width row under the payment (PaymentActionForm), never squeezed into the cell.
 */
export function PaymentActions({ p, onOpen, className = 'mt-1.5' }: { p: RefundablePayment; onOpen: (a: PaymentAction) => void; className?: string }) {
  const received = p.status === 'confirmed' || p.status === 'paid_out';
  if (!received) return null;
  return (
    <span className={`flex flex-wrap items-start gap-1.5 ${className}`}>
      {p.outstanding_pence > 0 && <button className={ROW_ACTION} onClick={() => onOpen('balance')}><Banknote size={12} /> Record balance ({money(p.outstanding_pence)} outstanding)…</button>}
      {p.method !== 'waived' && p.amount_pence > 0 && <button className={ROW_ACTION} onClick={() => onOpen('refund')}><RotateCcw size={12} /> Refund…</button>}
    </span>
  );
}

/** The refund or record-the-rest form for one payment, laid out across the table. */
export function PaymentActionForm({ p, action, onClose, onDone }: { p: RefundablePayment; action: PaymentAction; onClose: () => void; onDone: () => void }) {
  const qc = useQueryClient();
  const done = () => { qc.invalidateQueries({ queryKey: ['refundable', p.payment_id] }); onDone(); onClose(); };
  // On a phone the table scrolls sideways; keep the form the width of the screen and still while it does.
  return (
    <div className="sticky left-0 w-[min(100%,calc(100vw-4.5rem))]">
      {action === 'refund' ? <RefundForm payment={p} onDone={done} onCancel={onClose} />
        : <SettleForm part="amount" count={1} title="Record balance payment" owedPence={p.outstanding_pence} onCancel={onClose} onSave={async v => {
            const { error } = await supabase.rpc('admin_record_balance', { p_payment: p.payment_id, ...settleArgs(v), p_amount_pence: v.provider === 'waived' ? null : (v.amount ? toPence(v.amount) : p.outstanding_pence) });
            if (error) return settleError(error.message);
            done();
            return null;
          }} />}
    </div>
  );
}
